<script>
import CompanySelect from "./CompanySelect";
import NotificationAPI from "@/services/notifications/notificationsAPI";
import { menuItems, menuGroups, menuIcons } from "./horizontal-menu";
import { canAccessReports, ROLES } from "@/helpers/roles";

const STORAGE_KEY = "sst.rail.collapsed";
const FOCUS_KEY = "sst.rail.focus";

// Subrutas que no cuelgan del prefijo del enlace del riel: ítem del riel al que pertenecen
// (marca el ítem como sección activa y completa las migas). Q-006.
const SECTION_ROUTES = {
  "/forms/listado-empresa": [
    /^\/forms\/editar-empresa\//,
    /^\/forms\/editar-(colaboradores|sede)\//,
    /^\/forms\/[^/]+\/(registro-colaboradores|listado-sedes|registro-sede|responsable-sg)$/,
    /^\/forms\/responsable-sg\//,
  ],
  "/forms/users": [/^\/forms\/(registro-rol|editar-rol|permissions)(\/|$)/],
  "/forms/estandares": [/^\/forms\/(registro-item|editar-item)\b/],
};
const MOBILE_QUERY = "(max-width: 991.98px)";

const ROLE_LABELS = {
  1: "Super administrador",
  2: "Diseño SST",
  3: "Soportista",
  4: "Compañía",
  5: "Empleado",
  6: "Cliente administrador",
  7: "Talento humano",
  8: "Responsable operativo",
};

function readJSON(key) {
  try {
    return JSON.parse(localStorage.getItem(key)) || {};
  } catch (e) {
    return {};
  }
}

/**
 * AppShell: riel lateral (248 px, colapsable a 72 px; cajón en < 992 px),
 * barra superior de 56 px y un único <main>. Spec: docs/sdd/03-spec-shell-login.md
 * Mantiene la API de layouts/main.vue: el contenido de la vista entra por el slot.
 */
export default {
  name: "AppShell",
  components: { CompanySelect },
  data() {
    return {
      collapsed: false,
      isMobile: false,
      drawerOpen: false,
      openMenu: null,
      tip: { show: false, text: "", top: 0, left: 0 },

      // Sesión (mismas fuentes que Topbar.vue / Horizontal-navbar.vue)
      user: readJSON("user"),
      info: readJSON("info"),

      // Notificaciones (misma lógica que Topbar.vue)
      unread: 0,
      notifications: [],
      mq: null,
    };
  },
  computed: {
    company() {
      return this.$store.getters["company/getCompany"] || {};
    },
    companyName() {
      const c = this.company;
      if (!c || !c.id) return "";
      return c.social_reason || "Empresa " + (c.uuid || c.id);
    },
    userName() {
      const nodexum = this.user && this.user.nodexum;
      return (nodexum && nodexum.name) || "Usuario";
    },
    userEmail() {
      const nodexum = this.user && this.user.nodexum;
      return (nodexum && nodexum.email) || "";
    },
    roleLabel() {
      return ROLE_LABELS[Number(this.info.role)] || "Usuario";
    },
    initials() {
      const nodexum = this.user && this.user.nodexum;
      if (!nodexum || !nodexum.name) return "US";
      const parts = nodexum.name.trim().toUpperCase().split(/\s+/);
      if (parts.length > 1) return parts[0].charAt(0) + parts[1].charAt(0);
      return parts[0].charAt(0) + (parts[0].charAt(1) || "");
    },
    /**
     * Mismos filtros que `knowUserRole()` de Horizontal-navbar.vue:
     *  - Usuarios: solo si el usuario SSO tiene el rol "root"
     *  - Reporte: solo canAccessReports(role)
     *  - Talento Humano: oculta PHVA, Gestión empresa y Base de conocimiento
     * (`hidden` ya viene aplicado en `menuItems`).
     */
    visibleItems() {
      const sstRole = Number(this.info.role);
      const roles =
        (this.user && this.user.nodexum && this.user.nodexum.roles) || [];
      const isRoot = roles.some((role) => role.name === "root");

      let items = menuItems;
      if (!isRoot) items = items.filter((item) => item.label !== "Usuarios");
      if (!canAccessReports(sstRole))
        items = items.filter((item) => item.label !== "Reporte");
      if (sstRole === ROLES.TALENTO_HUMANO) {
        const hideForTH = ["PHVA", "Gestión empresa", "Base de conocimiento"];
        items = items.filter((item) => !hideForTH.includes(item.label));
      }
      return items;
    },
    groups() {
      const items = this.visibleItems;
      return menuGroups
        .map((group) => ({
          id: group.id,
          label: group.label,
          items: group.links
            .map((link) => items.find((item) => item.link === link))
            .filter(Boolean)
            .map((item) => ({
              label: item.label,
              link: item.link,
              icon: menuIcons[item.link] || item.icon,
            })),
        }))
        .filter((group) => group.items.length > 0);
    },
    currentPath() {
      return this.$route.path.replace(/(.)\/+$/, "$1");
    },
    activeEntry() {
      let found = null;
      this.groups.forEach((group) => {
        group.items.forEach((item) => {
          if (this.isExact(item) || (!found && this.isSection(item))) {
            found = { group: group.label, item: item };
          }
        });
      });
      return found;
    },
    crumbs() {
      if (!this.activeEntry) return [{ label: "SG-SST" }];
      const { group, item } = this.activeEntry;
      const list = [{ label: "SG-SST" }];
      if (item.link !== "/") list.push({ label: group });
      list.push({ label: item.label, current: true });
      return list;
    },
    toggleLabel() {
      if (this.isMobile) return this.drawerOpen ? "Cerrar menú" : "Abrir menú";
      return this.collapsed ? "Expandir menú lateral" : "Contraer menú lateral";
    },
    toggleExpanded() {
      return this.isMobile ? this.drawerOpen : !this.collapsed;
    },
    bellLabel() {
      return this.unread
        ? "Notificaciones, " + this.unread + " sin leer"
        : "Notificaciones";
    },
    year() {
      return new Date().getFullYear();
    },
  },
  watch: {
    $route() {
      this.drawerOpen = false;
      this.openMenu = null;
    },
    drawerOpen(open) {
      try {
        document.body.style.overflow = open ? "hidden" : "";
      } catch (e) {
        /* sin DOM */
      }
    },
  },
  created() {
    try {
      this.collapsed = localStorage.getItem(STORAGE_KEY) === "1";
    } catch (e) {
      this.collapsed = false;
    }
  },
  mounted() {
    // Limpia atributos de layout que dejaron los layouts Minton (horizontal/vertical).
    ["data-layout-mode", "data-layout-width", "data-layout-menu-position",
      "data-sidebar-size", "data-sidebar-showuser", "data-topbar-color"].forEach(
      (attr) => document.body.removeAttribute(attr)
    );
    document.body.classList.remove("right-bar-enabled", "sidebar-enable");

    if (window.matchMedia) {
      this.mq = window.matchMedia(MOBILE_QUERY);
      this.isMobile = this.mq.matches;
      if (this.mq.addEventListener) this.mq.addEventListener("change", this.onMq);
      else if (this.mq.addListener) this.mq.addListener(this.onMq);
    }
    document.addEventListener("click", this.onDocClick);
    document.addEventListener("keydown", this.onDocKeydown);

    this.getAllNotifications();
    this.restoreFocus();
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocClick);
    document.removeEventListener("keydown", this.onDocKeydown);
    if (this.mq) {
      if (this.mq.removeEventListener) this.mq.removeEventListener("change", this.onMq);
      else if (this.mq.removeListener) this.mq.removeListener(this.onMq);
    }
    try {
      document.body.style.overflow = "";
    } catch (e) {
      /* sin DOM */
    }
  },
  methods: {
    // Las etiquetas son texto en español, no claves de i18n: traduce solo si existe la clave.
    label(text) {
      return this.$te(text) ? this.$t(text) : text;
    },
    // ── Navegación ─────────────────────────────────────────────────────────
    isExact(item) {
      return this.currentPath === item.link;
    },
    isSection(item) {
      if (item.link === "/") return false;
      if (this.currentPath.indexOf(item.link + "/") === 0) return true;
      const rules = SECTION_ROUTES[item.link];
      return !!rules && rules.some((re) => re.test(this.currentPath));
    },
    ariaCurrent(item) {
      if (this.isExact(item)) return "page";
      return this.isSection(item) ? "true" : null;
    },
    go(event, link) {
      // Deja pasar ctrl/cmd/shift/clic medio: abre en pestaña nueva con el href.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      event.preventDefault();
      this.hideTip();
      if (this.currentPath === link) {
        this.drawerOpen = false;
        return;
      }
      if (event.detail === 0) {
        // Navegación por teclado: cada vista monta su propio layout, así que se
        // devuelve el foco al ítem activo tras el cambio de página.
        try {
          sessionStorage.setItem(FOCUS_KEY, "1");
        } catch (e) {
          /* sin storage */
        }
      }
      const result = this.$router.push(link);
      if (result && result.catch) {
        // Navegación cancelada (guard): no debe quedar la bandera de foco
        result.catch(() => {
          try {
            sessionStorage.removeItem(FOCUS_KEY);
          } catch (e) {
            /* sin storage */
          }
        });
      }
    },
    restoreFocus() {
      let flag = null;
      try {
        flag = sessionStorage.getItem(FOCUS_KEY);
        sessionStorage.removeItem(FOCUS_KEY);
      } catch (e) {
        /* sin storage */
      }
      if (!flag) return;
      this.$nextTick(() => {
        const el = this.$el.querySelector('.sst-nav__link[aria-current="page"]');
        if (el) el.focus({ preventScroll: true });
      });
    },

    // ── Riel y cajón ───────────────────────────────────────────────────────
    onMq(e) {
      this.isMobile = e.matches;
      if (!e.matches) this.drawerOpen = false;
    },
    toggleRail() {
      if (this.isMobile) {
        if (this.drawerOpen) this.closeDrawer();
        else this.openDrawer();
        return;
      }
      this.collapsed = !this.collapsed;
      this.hideTip();
      try {
        localStorage.setItem(STORAGE_KEY, this.collapsed ? "1" : "0");
      } catch (e) {
        /* sin storage */
      }
    },
    openDrawer() {
      this.drawerOpen = true;
      this.openMenu = null;
      // El riel pasa de visibility:hidden a visible; con movimiento reducido esa transición dura
      // un fotograma y el foco fallaría. Si no entró, se reintenta tras el siguiente fotograma.
      const focusClose = () => {
        const close = this.$refs.close;
        if (close) close.focus();
        return !!close && document.activeElement === close;
      };
      this.$nextTick(() => {
        if (focusClose()) return;
        requestAnimationFrame(() => {
          if (this.drawerOpen && !focusClose()) setTimeout(focusClose, 50);
        });
      });
    },
    closeDrawer(returnFocus) {
      this.drawerOpen = false;
      if (returnFocus !== false) {
        this.$nextTick(() => {
          if (this.$refs.toggle) this.$refs.toggle.focus();
        });
      }
    },
    onRailKeydown(e) {
      if (!this.drawerOpen || e.key !== "Tab") return;
      const nodes = this.$refs.rail.querySelectorAll(
        "a[href], button:not([disabled])"
      );
      const list = Array.prototype.filter.call(
        nodes,
        (n) => n.offsetParent !== null
      );
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    focusMain(e) {
      e.preventDefault();
      const main = this.$refs.main;
      if (main) {
        main.focus({ preventScroll: false });
        main.scrollIntoView();
      }
    },

    // ── Tooltips del riel colapsado ────────────────────────────────────────
    showTip(event, text) {
      if (this.isMobile || !this.collapsed) return;
      const rect = event.currentTarget.getBoundingClientRect();
      this.tip = {
        show: true,
        text: text,
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      };
    },
    hideTip() {
      if (this.tip.show) this.tip = { ...this.tip, show: false };
    },

    // ── Menús (notificaciones y usuario) ───────────────────────────────────
    toggleMenu(name) {
      this.openMenu = this.openMenu === name ? null : name;
    },
    closeMenus(returnFocus) {
      const name = this.openMenu;
      this.openMenu = null;
      if (returnFocus && name) {
        const btn = this.$refs[name + "Btn"];
        if (btn) btn.focus();
      }
    },
    onDocClick(e) {
      const area = this.$refs.menus;
      if (this.openMenu && area && !area.contains(e.target)) this.closeMenus();
    },
    onDocKeydown(e) {
      if (e.key !== "Escape") return;
      if (this.tip.show) this.hideTip();
      if (this.openMenu) {
        this.closeMenus(true);
      } else if (this.drawerOpen) {
        this.closeDrawer();
      }
    },
    onMenusFocusout(e) {
      const area = this.$refs.menus;
      const next = e.relatedTarget;
      if (this.openMenu && next && area && !area.contains(next)) this.closeMenus();
    },

    // ── Empresa activa ─────────────────────────────────────────────────────
    changeCompany() {
      this.hideTip();
      this.$bvModal.show("my-company");
    },

    // ── Notificaciones (misma lógica que Topbar.vue) ───────────────────────
    getAllNotifications() {
      const companyID = this.company.id || null;
      const auth = this.$store.state.auth || {};
      const authInfo = auth.info || this.info;
      const setList = (response) => {
        const datos =
          (response && response.Notification && response.Notification.data) || [];
        this.notifications = datos.length > 5 ? datos.slice(0, 5) : datos;
      };
      const setUnread = (response) => {
        this.unread =
          (response && response.Notification && response.Notification.length) || 0;
      };

      if (authInfo.role !== 2) {
        NotificationAPI.unread(companyID)
          .then(setUnread)
          .catch((error) => console.log(error));
        NotificationAPI.getAllNotifications(companyID)
          .then(setList)
          .catch((error) => console.log(error));
      } else {
        NotificationAPI.unreadByUser(companyID, authInfo.sstID)
          .then(setUnread)
          .catch((error) => console.log(error));
        NotificationAPI.getByUser(companyID, authInfo.sstID)
          .then(setList)
          .catch((error) => console.log(error));
      }
    },
    notificationText(dato) {
      const text = dato && dato.message && dato.message.message;
      if (!text) return "";
      const name = this.company.social_reason || "";
      return text
        .replace("Nombreempresa", name)
        .replace("xxx", name)
        .replace(":nombreEmpresa", name);
    },
    goNotifications() {
      this.openMenu = null;
      const result = this.$router.push("/forms/notifications-list");
      if (result && result.catch) {
        // Navegación cancelada (guard): no debe quedar la bandera de foco
        result.catch(() => {
          try {
            sessionStorage.removeItem(FOCUS_KEY);
          } catch (e) {
            /* sin storage */
          }
        });
      }
    },

    // ── Pantalla completa (igual que Topbar.vue) ───────────────────────────
    initFullScreen() {
      document.body.classList.toggle("fullscreen-enable");
      const doc = document;
      const el = doc.documentElement;
      if (
        !doc.fullscreenElement &&
        !doc.mozFullScreenElement &&
        !doc.webkitFullscreenElement
      ) {
        if (el.requestFullscreen) el.requestFullscreen();
        else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
        else if (el.webkitRequestFullscreen)
          el.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      } else if (doc.cancelFullScreen) doc.cancelFullScreen();
      else if (doc.mozCancelFullScreen) doc.mozCancelFullScreen();
      else if (doc.webkitCancelFullScreen) doc.webkitCancelFullScreen();
    },
  },
};
</script>

<template>
  <div
    class="sst-app"
    :class="{ 'is-collapsed': collapsed, 'is-drawer-open': drawerOpen }"
  >
    <a href="#sst-main" class="sst-skip" @click="focusMain">Saltar al contenido</a>

    <!-- Riel lateral / cajón -->
    <aside
      id="sst-rail"
      ref="rail"
      class="sst-rail"
      :class="{ 'is-open': drawerOpen }"
      :role="isMobile && drawerOpen ? 'dialog' : null"
      :aria-modal="isMobile && drawerOpen ? 'true' : null"
      :aria-label="isMobile && drawerOpen ? 'Menú' : null"
      @keydown="onRailKeydown"
    >
      <div class="sst-rail__head">
        <a
          href="/"
          class="sst-brand"
          aria-label="SG-SST, ir al inicio"
          @click="go($event, '/')"
          @mouseenter="showTip($event, 'SG-SST · Inicio')"
          @mouseleave="hideTip"
          @focus="showTip($event, 'SG-SST · Inicio')"
          @blur="hideTip"
        >
          <!-- Símbolo: rueda PHVA (Planear, Hacer, Verificar, Actuar) en 4 cuadrantes -->
          <svg
            class="sst-brand__mark"
            viewBox="0 0 32 32"
            width="32"
            height="32"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M2 16A14 14 0 0 1 16 2V10A6 6 0 0 0 10 16Z" transform="translate(-1 -1)" fill="var(--sst-brand-700)" />
            <path d="M16 2A14 14 0 0 1 30 16H22A6 6 0 0 0 16 10Z" transform="translate(1 -1)" fill="var(--sst-brand)" />
            <path d="M30 16A14 14 0 0 1 16 30V22A6 6 0 0 0 22 16Z" transform="translate(1 1)" fill="var(--sst-seq-500)" />
            <path d="M16 30A14 14 0 0 1 2 16H10A6 6 0 0 0 16 22Z" transform="translate(-1 1)" fill="var(--sst-brand-300)" />
          </svg>
          <span class="sst-brand__name">SG-SST</span>
        </a>
        <button
          ref="close"
          type="button"
          class="sst-iconbtn sst-rail__close"
          aria-label="Cerrar menú"
          @click="closeDrawer()"
        >
          <i class="ri-close-line" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Empresa activa -->
      <div class="sst-company" :class="{ 'is-empty': !companyName }">
        <div class="sst-company__text">
          <span class="sst-company__label">Empresa activa</span>
          <span class="sst-company__name" :title="companyName">{{
            companyName || "Sin empresa seleccionada"
          }}</span>
        </div>
        <button
          type="button"
          class="sst-company__change"
          :aria-label="companyName ? 'Cambiar empresa activa: ' + companyName : 'Seleccionar empresa'"
          @click="changeCompany"
          @mouseenter="showTip($event, companyName ? 'Cambiar empresa · ' + companyName : 'Seleccionar empresa')"
          @mouseleave="hideTip"
          @focus="showTip($event, companyName ? 'Cambiar empresa · ' + companyName : 'Seleccionar empresa')"
          @blur="hideTip"
        >
          <i class="ri-arrow-left-right-line sst-company__icon" aria-hidden="true"></i>
          <span class="sst-company__action">{{ companyName ? "Cambiar" : "Elegir" }}</span>
        </button>
      </div>

      <!-- Navegación agrupada -->
      <nav class="sst-nav" aria-label="Principal">
        <div v-for="group in groups" :key="group.id" class="sst-nav__group">
          <p :id="'sst-g-' + group.id" class="sst-nav__label">{{ group.label }}</p>
          <ul class="sst-nav__list" :aria-labelledby="'sst-g-' + group.id">
            <li v-for="item in group.items" :key="item.link">
              <a
                :href="item.link"
                class="sst-nav__link"
                :class="{ 'is-active': isExact(item), 'is-section': !isExact(item) && isSection(item) }"
                :aria-current="ariaCurrent(item)"
                :aria-label="label(item.label)"
                @click="go($event, item.link)"
                @mouseenter="showTip($event, label(item.label))"
                @mouseleave="hideTip"
                @focus="showTip($event, label(item.label))"
                @blur="hideTip"
              >
                <i :class="item.icon" class="sst-nav__icon" aria-hidden="true"></i>
                <span class="sst-nav__text">{{ label(item.label) }}</span>
              </a>
            </li>
          </ul>
        </div>
      </nav>

      <!-- Pie del riel: usuario + rol -->
      <div class="sst-rail__foot">
        <div
          class="sst-user"
          @mouseenter="showTip($event, userName + ' · ' + roleLabel)"
          @mouseleave="hideTip"
        >
          <span class="sst-avatar" aria-hidden="true">{{ initials }}</span>
          <span class="sst-user__text">
            <span class="sst-user__name" :title="userName">{{ userName }}</span>
            <span class="sst-user__role">{{ roleLabel }}</span>
          </span>
        </div>
      </div>
    </aside>

    <div class="sst-scrim" :class="{ 'is-on': drawerOpen }" @click="closeDrawer()"></div>

    <!-- Tooltip del riel colapsado (posición fija: el riel hace scroll) -->
    <div
      class="sst-tip"
      :class="{ 'is-on': tip.show }"
      :style="{ top: tip.top + 'px', left: tip.left + 'px' }"
      aria-hidden="true"
    >{{ tip.text }}</div>

    <div class="sst-stage">
      <!-- Barra superior -->
      <header class="sst-topbar">
        <button
          ref="toggle"
          type="button"
          class="sst-iconbtn sst-topbar__toggle"
          :aria-label="toggleLabel"
          :aria-expanded="String(toggleExpanded)"
          aria-controls="sst-rail"
          @click="toggleRail"
        >
          <i :class="isMobile ? 'ri-menu-line' : 'ri-side-bar-line'" aria-hidden="true"></i>
        </button>

        <nav class="sst-crumbs" aria-label="Ruta de navegación">
          <ol>
            <li v-for="(crumb, i) in crumbs" :key="i" :class="{ 'is-current': crumb.current }">
              <span :aria-current="crumb.current ? 'page' : null">{{ crumb.label }}</span>
            </li>
          </ol>
        </nav>

        <!-- Contexto de empresa en pantallas estrechas (el riel está oculto en el cajón) -->
        <span
          v-if="companyName"
          class="sst-topbar__company"
          :title="companyName"
        ><i class="ri-building-line" aria-hidden="true"></i><span class="sst-topbar__company-name"><span class="sr-only">Empresa activa: </span>{{ companyName }}</span></span>

        <div ref="menus" class="sst-topbar__actions" @focusout="onMenusFocusout">
          <button
            type="button"
            class="sst-iconbtn sst-topbar__fullscreen"
            aria-label="Pantalla completa"
            @click="initFullScreen"
          >
            <i class="ri-fullscreen-line" aria-hidden="true"></i>
          </button>

          <!-- Notificaciones -->
          <div class="sst-menu">
            <button
              ref="notifBtn"
              type="button"
              class="sst-iconbtn sst-bell"
              aria-haspopup="true"
              :aria-expanded="String(openMenu === 'notif')"
              aria-controls="sst-notif-panel"
              :aria-label="bellLabel"
              @click="toggleMenu('notif')"
            >
              <i class="fe-bell" aria-hidden="true"></i>
              <span v-if="unread" class="sst-bell__count" aria-hidden="true">{{ unread > 99 ? "99+" : unread }}</span>
            </button>
            <div
              v-show="openMenu === 'notif'"
              id="sst-notif-panel"
              class="sst-panel sst-panel--notif"
              role="region"
              aria-label="Notificaciones"
            >
              <p class="sst-panel__title">Notificaciones</p>
              <ul v-if="notifications.length" class="sst-panel__list">
                <li v-for="dato in notifications" :key="dato.id">
                  <a
                    href="/forms/notifications-list"
                    class="sst-panel__item"
                    :class="{ 'is-read': dato.viewed }"
                    @click.prevent="goNotifications"
                  >
                    <span class="sst-panel__text">{{ notificationText(dato) }}</span>
                  </a>
                </li>
              </ul>
              <p v-else class="sst-panel__empty">No hay notificaciones que mostrar</p>
              <a
                href="/forms/notifications-list"
                class="sst-panel__all"
                @click.prevent="goNotifications"
              >Ver todas las notificaciones</a>
            </div>
          </div>

          <!-- Usuario -->
          <div class="sst-menu">
            <button
              ref="userBtn"
              type="button"
              class="sst-userbtn"
              aria-haspopup="true"
              :aria-expanded="String(openMenu === 'user')"
              aria-controls="sst-user-panel"
              :aria-label="'Menú de usuario: ' + userName"
              @click="toggleMenu('user')"
            >
              <span class="sst-avatar" aria-hidden="true">{{ initials }}</span>
              <span class="sst-userbtn__name">{{ userName }}</span>
              <i class="ri-arrow-down-s-line sst-userbtn__chev" aria-hidden="true"></i>
            </button>
            <div
              v-show="openMenu === 'user'"
              id="sst-user-panel"
              class="sst-panel sst-panel--user"
              role="region"
              aria-label="Cuenta"
            >
              <div class="sst-panel__who">
                <span class="sst-panel__name">{{ userName }}</span>
                <span v-if="userEmail" class="sst-panel__mail">{{ userEmail }}</span>
                <span class="sst-panel__role">{{ roleLabel }}</span>
              </div>
              <a href="/logout" class="sst-panel__logout">
                <i class="ri-logout-box-r-line" aria-hidden="true"></i>
                <span>{{ label("Salir") }}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <main id="sst-main" ref="main" class="sst-main" tabindex="-1">
        <div class="sst-content">
          <slot />
        </div>
      </main>

      <footer class="sst-footer">
        <div class="sst-footer__in">{{ year }} &copy; Hecho por Emisión</div>
      </footer>
    </div>

    <!-- CompanySelect conserva el modal "my-company", la carga de empresas y la
         selección automática al montar; su disparador se oculta y se abre desde "Cambiar". -->
    <div class="sst-company-host" hidden>
      <CompanySelect />
    </div>
  </div>
</template>
